import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppFooter } from '../components/AppFooter';
import { getErrorMessage } from '../utils/getErrorMessage';
import '../styles/LoginPage.css';
import logo from '../assets/logo.png';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/auth/forgot-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error?.message || 'Failed to send reset email');
      }

      setSubmitted(true);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src={logo} alt="Belafonte TACOLCY Center" className="login-logo" />
            <h2 className="login-title">Check Your Email</h2>
          </div>

          <div className="login-form">
            <div style={{
              padding: '1.5rem',
              background: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              marginBottom: '1.5rem'
            }}>
              <p style={{ color: '#155724', marginBottom: '1rem' }}>
                If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
              </p>
              <p style={{ color: '#155724', marginBottom: 0, fontSize: '0.9rem' }}>
                Please check your email inbox (and spam folder) for the reset link. The link will expire in 24 hours.
              </p>
            </div>

            <Link to="/login">
              <button className="button-primary login-button">
                Back to Login
              </button>
            </Link>

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setEmail('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#5B2C86',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '0.95rem'
                }}
              >
                Try a different email
              </button>
            </div>
          </div>

          <AppFooter />
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src={logo} alt="Belafonte TACOLCY Center" className="login-logo" />
          <h2 className="login-title">Forgot Password?</h2>
          <p className="login-subtitle">Enter your email address and we&apos;ll send you a link to reset your password.</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com"
            />
          </div>

          <button
            type="submit"
            className="button-primary login-button"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>

          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <Link to="/login" style={{ color: '#5B2C86', textDecoration: 'none', fontSize: '0.95rem' }}>
              ← Back to Login
            </Link>
          </div>
        </form>

        <AppFooter />
      </div>
    </div>
  );
}
